import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import type { ForwardedRef } from 'react';
import {
  GridReadyEvent,
  PaginationChangedEvent,
  ColumnEverythingChangedEvent,
} from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import classnames from 'classnames';
import { Loading } from '@glu/indicators-react';
import { AccessibilityText } from '@glu/utilities-react';
import locale from '@glu/locale';
import { LicenseManager } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.min.css';
import 'ag-grid-community/styles/ag-theme-material.min.css';
import { RowActions } from '@glu/data-components';
import { useComposedGridContext } from 'ComposedGrid/ComposedGridContext';
import HeaderCell from 'HeaderCell/HeaderCell';
import useStyles from './Grid.styles';

LicenseManager.setLicenseKey(
  'CompanyName=Bottomline Technologies (de), Inc.,LicensedGroup=Guild,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=10,LicensedProductionInstancesCount=2,AssetReference=AG-032933,SupportServicesEnd=7_November_2023_[v2]_MTY5OTMxNTIwMDAwMA==96b047a42fc0a45dcec2ea7c1c8af1de'
);

const noop = () => null;

export interface IExtensionProps {
  /** The id of the grid, used to set an id on the wrapping div */
  gridId?: string;
  /**
   * Will resize columns to fit the content automatically onMount, when the window size changes,
   * and when any changes are made to the columns (i.e. hiding a columns)
   */
  sizeColumnsToFit?: boolean;
  /** The height of the grid. Used to set the height of the container */
  gridHeight?: number;
  /** whether the header sticks to the top of the window */
  headerSticky?: boolean;
}

export interface IGridProps<D> extends AgGridReactProps<D>, IExtensionProps {}

const builtInComponents = {
  actions: RowActions,
  agColumnHeader: HeaderCell,
};

const LoadingOverlayComponent = () => (
  <Loading isLoading>
    <AccessibilityText>{locale.get('gridReact.loading')}</AccessibilityText>
  </Loading>
);

function Grid<D>(
  {
    components: passedComponents,
    domLayout,
    gridHeight,
    gridId,
    headerSticky,
    onColumnEverythingChanged: onColumnEverythingChangedPassed,
    onGridReady: onGridReadyPassed,
    onPaginationChanged: onPaginationChangedPassed,
    paginationPageSize,
    sizeColumnsToFit,
    ...props
  }: IGridProps<D>,
  ref: ForwardedRef<AgGridReact<D>>
) {
  const { gridRef, setReady } = useComposedGridContext();

  useImperativeHandle(ref, () => gridRef.current);
  const classes = useStyles();
  const rootStyles = useMemo(
    () => ({ height: domLayout === 'autoHeight' ? 'auto' : gridHeight }),
    [domLayout, gridHeight]
  );

  const localeText = useMemo(
    () => ({
      noRowsToShow: locale.get('gridReact.noContent'),
    }),
    []
  );

  const components = useMemo(
    () => ({ ...builtInComponents, ...passedComponents }),
    [passedComponents]
  );

  /** Resize column functionality */
  const resize = useCallback(() => gridRef.current.api.sizeColumnsToFit(), [gridRef]);
  const onFirstDataRendered = useCallback(() => {
    if (sizeColumnsToFit) {
      resize();
    }
  }, [resize, sizeColumnsToFit]);
  const onGridReady = useCallback(
    (params: GridReadyEvent<D>) => {
      onGridReadyPassed?.(params);
      setReady();
    },
    [onGridReadyPassed, setReady]
  );

  const onPaginationChanged = useCallback(
    (params: PaginationChangedEvent) => {
      if (onPaginationChangedPassed) {
        onPaginationChangedPassed(params);
      }
      if (sizeColumnsToFit) {
        resize();
      }
    },
    [onPaginationChangedPassed, resize, sizeColumnsToFit]
  );

  const onColumnEverythingChanged = useCallback(
    (params: ColumnEverythingChangedEvent<D>) => {
      if (onColumnEverythingChangedPassed) {
        onColumnEverythingChangedPassed(params);
      }
      if (sizeColumnsToFit) {
        resize();
      }
    },
    [onColumnEverythingChangedPassed, resize, sizeColumnsToFit]
  );

  useEffect(() => {
    if (!sizeColumnsToFit) return () => null;
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [resize, sizeColumnsToFit]);
  return (
    <div
      className={classnames(classes.root, 'ag-theme-material', 'ag-theme-glu', {
        [classes.headerSticky]: headerSticky,
      })}
      id={`${gridId}-popover-wrapper`}
      style={rootStyles}
    >
      <AgGridReact<D>
        ref={gridRef}
        animateRows
        cacheBlockSize={paginationPageSize}
        components={components}
        domLayout={domLayout}
        gridId={gridId}
        headerHeight={40}
        loadingCellRenderer={noop}
        loadingOverlayComponent={LoadingOverlayComponent}
        localeText={localeText}
        onColumnEverythingChanged={onColumnEverythingChanged}
        onFirstDataRendered={onFirstDataRendered}
        onGridReady={onGridReady}
        onPaginationChanged={onPaginationChanged}
        paginationPageSize={paginationPageSize}
        /**
         * The reason this is in here is so that we can support v28 and v29 of ag-grid without introducing
         * a breaking change to @glu/grid-react. In the future, we will remove this prop. In v29 of ag-grid,
         * ag-grid made serverSideInfiniteScroll the default. If you're using v29, this prop will be ignored.
         * If you're using v28, this prop will continue to be respected.
         */
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        serverSideInfiniteScroll
        suppressPaginationPanel
        suppressPropertyNamesCheck
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    </div>
  );
}

export default forwardRef(Grid);
